import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAsyncAction } from '../hooks/useAsyncAction';
import Wordmark from '../components/Wordmark';
import PasswordInput from '../components/PasswordInput';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  const flash = location.state?.flash || '';

  const [form, setForm] = useState({ email: '', password: '' });
  const { loading, error, run } = useAsyncAction();

  const handleSubmit = async (e) => {
    e.preventDefault();
    await run(async () => {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    });
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}><Wordmark style={{ fontSize: '20px' }} /></div>
        <h1 style={styles.heading}>Welcome back</h1>
        <p style={styles.sub}>Log in to your teacher account</p>

        {flash && <div style={styles.flash}>{flash}</div>}
        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            Email
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              style={styles.input}
              required
              autoComplete="email"
            />
          </label>
          <label style={styles.label}>
            Password
            <PasswordInput
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              autoComplete="current-password"
              inputStyle={{ color: 'var(--color-text)', backgroundColor: 'var(--color-background)' }}
            />
          </label>
          <div style={{ textAlign: 'right', marginTop: '-8px' }}>
            <Link to="/forgot-password" style={styles.link}>Forgot password?</Link>
          </div>
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Logging in…' : 'Log in'}
          </button>
        </form>

        <div style={styles.footer}>
          Don't have an account?{' '}
          <Link to="/signup" style={styles.link}>Sign up for $19.99/yr</Link>
        </div>
        <div style={{ ...styles.footer, marginTop: '8px' }}>
          Have a TpT printable?{' '}
          <Link to="/play" style={styles.link}>Access your free novel →</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-background)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    backgroundColor: 'var(--color-background)',
    border: '1px solid #e5e0d5',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  logo: {
    marginBottom: '24px',
    letterSpacing: '-0.3px',
  },
  heading: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--color-text)',
    margin: '0 0 4px',
  },
  sub: {
    fontSize: '14px',
    color: '#6b6b7a',
    margin: '0 0 24px',
  },
  flash: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    color: '#15803d',
    marginBottom: '16px',
  },
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #d9d4c5',
    borderRadius: '8px',
    fontSize: '15px',
    color: 'var(--color-text)',
    backgroundColor: 'var(--color-background)',
    outline: 'none',
  },
  button: {
    padding: '12px',
    backgroundColor: 'var(--color-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  footer: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#6b6b7a',
    marginTop: '20px',
  },
  link: {
    color: 'var(--color-primary)',
    textDecoration: 'none',
    fontWeight: '500',
  },
};
